import { GripVertical } from "lucide-react";
import type { Payment } from "../types/payments.types";
import { formatPaymentAmount } from "../utils/formatPaymentAmount";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

type PaymentCardPreviewProps = {
  payment: Payment;
  isOverlay?: boolean;
};

const getStatusConfig = (t: (k: keyof import("@/shared/lang/interfaces/payments").PaymentsTranslations) => string) => ({
  pending: {
    borderClass: "border-l-blue-300 dark:border-l-blue-400",
    badgeClass:
      "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
    label: t("card_status_pending"),
  },
  in_progress: {
    borderClass: "border-l-amber-300 dark:border-l-amber-400",
    badgeClass:
      "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400",
    label: t("card_status_in_progress"),
  },
  paid: {
    borderClass: "border-l-green-300 dark:border-l-green-400",
    badgeClass:
      "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
    label: t("card_status_paid"),
  },
} as const);

export const PaymentCardPreview = ({
  payment,
  isOverlay = true,
}: PaymentCardPreviewProps) => {
  const { t } = useTranslations("payments");
  const statusConfig = getStatusConfig(t);
  const config = statusConfig[payment.status];

  return (
    <div
      role="presentation"
      data-payment-id={payment.id}
      className={cn(
        "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl border-l-4 pointer-events-none",
        config.borderClass,
        isOverlay &&
          "shadow-xl ring-2 ring-green-500/30 scale-[1.02] cursor-grabbing"
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="shrink-0 mt-0.5 text-slate-400 dark:text-slate-500"
            aria-hidden
          >
            <GripVertical className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h4 className="font-bold text-slate-900 dark:text-white truncate">
              {payment.name}
            </h4>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {payment.currency} {formatPaymentAmount(payment.amount)}
            </p>
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight",
            config.badgeClass
          )}
        >
          {config.label}
        </span>
      </div>

      <div className="space-y-1.5 text-sm text-slate-500 dark:text-slate-400">
        <div className="flex justify-between gap-2">
          <span>{t("preview_payer")}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
            {payment.payer}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span>{t("preview_platform")}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
            {payment.platform}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span>{t("preview_date")}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {formatDateTime(payment.date)}
          </span>
        </div>
        {payment.createdBy && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[11px] opacity-75">
              {t("preview_created_by")} {payment.createdBy}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
